import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Container, Box, CircularProgress, Alert } from '@mui/material';
import './App.css';

// Import Auth0 components
import { useAuth0 } from '@auth0/auth0-react';

// Import our components
import ReturnsList from './components/ReturnsList';
import ReturnForm from './components/ReturnForm';
import ReturnDetail from './components/ReturnDetail';
import CustomersList from './components/CustomersList';
import CustomerForm from './components/CustomerForm';
import SellersList from './components/SellersList';
import SellerForm from './components/SellerForm';
import Home from './components/Home';
import Login from './components/Login';
import NavBar from './components/NavBar';
import Callback from './components/Callback';
import AdminPanel from './components/AdminPanel';

function App() {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();
  const [authStatus, setAuthStatus] = useState({
    isAuthorized: false,
    isAdmin: false,
    isChecking: true,
    error: null
  });

  // Check if the user is authorized and if they are an admin
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (!isAuthenticated || !user) return;
      
      // Debug: Log the user object from Auth0
      console.log('Auth0 user object:', user);
      console.log('Auth0 user email:', user.email);
      
      try {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        
        // Include the email as a query parameter
        const email = user.email;
        // Fix URL construction to prevent double /api
        const url = apiUrl === '/api' 
          ? `/api/auth/check?email=${encodeURIComponent(email)}` 
          : `${apiUrl}/auth/check?email=${encodeURIComponent(email)}`;
        console.log('Fetching auth check from:', url);
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Response status:', response.status);
        
        // Handle different response statuses
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'You are not authorized to access this application');
        } else if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }
        
        // Parse the response only once
        const data = await response.json();
        console.log('Auth check response data:', data);
        
        setAuthStatus({
          isAuthorized: data.authorized,
          isAdmin: data.isAdmin,
          isChecking: false,
          error: null
        });
      } catch (error) {
        console.error('Authorization check failed:', error);
        setAuthStatus({
          isAuthorized: false,
          isAdmin: false,
          isChecking: false,
          error: error.message
        });
      }
    };
    
    if (isAuthenticated) {
      checkAuthStatus();
    } else {
      setAuthStatus({
        isAuthorized: false,
        isAdmin: false,
        isChecking: false,
        error: null
      });
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);

  if (isLoading || (isAuthenticated && authStatus.isChecking)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Create a protected route component inline
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) {
      loginWithRedirect();
      return <CircularProgress />;
    }
    
    if (!authStatus.isAuthorized) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Sinulla ei ole oikeuksia käyttää tätä sovellusta. Ota yhteyttä järjestelmänvalvojaan.
          </Alert>
        </Box>
      );
    }
    
    return children;
  };
  
  // Create an admin-only route component
  const AdminRoute = ({ children }) => {
    if (!isAuthenticated) {
      loginWithRedirect();
      return <CircularProgress />;
    }
    
    if (!authStatus.isAuthorized) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Sinulla ei ole oikeuksia käyttää tätä sovellusta. Ota yhteyttä järjestelmänvalvojaan.
          </Alert>
        </Box>
      );
    }
    
    if (!authStatus.isAdmin) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Sinulla ei ole järjestelmänvalvojan oikeuksia.
          </Alert>
        </Box>
      );
    }
    
    return children;
  };

  return (
    <Router>
      <div className="App">
        <NavBar />

        <Container className="main-container" maxWidth={false}>
          <Box sx={{ mt: 4, mb: 4 }}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/callback" element={<Callback />} />
              <Route path="/" element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              } />
              <Route path="/new" element={
                <ProtectedRoute>
                  <ReturnForm />
                </ProtectedRoute>
              } />
              <Route path="/edit/:id" element={
                <ProtectedRoute>
                  <ReturnForm />
                </ProtectedRoute>
              } />
              <Route path="/customers" element={
                <ProtectedRoute>
                  <CustomersList />
                </ProtectedRoute>
              } />
              <Route path="/customers/new" element={
                <ProtectedRoute>
                  <CustomerForm />
                </ProtectedRoute>
              } />
              <Route path="/customers/edit/:id" element={
                <ProtectedRoute>
                  <CustomerForm />
                </ProtectedRoute>
              } />
              <Route path="/sellers" element={
                <ProtectedRoute>
                  <SellersList />
                </ProtectedRoute>
              } />
              <Route path="/sellers/new" element={
                <ProtectedRoute>
                  <SellerForm />
                </ProtectedRoute>
              } />
              <Route path="/sellers/edit/:id" element={
                <ProtectedRoute>
                  <SellerForm />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminPanel />
                </AdminRoute>
              } />
            </Routes>
          </Box>
        </Container>
      </div>
    </Router>
  );
}

export default App;
